import React, { useState, useMemo, useEffect } from 'react';
import { useAllCompetencies } from '../../api/CompetenciesAPI';

const styles = {
  container: {
    marginBottom: '15px',
  },
  competencyGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '15px',
    padding: '12px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  phaseGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px',
    marginRight: '0px',
    marginBottom: '8px',
    width: '100%',
  },
  competencyDot: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  competencyDotHover: {
    transform: 'scale(1.5)',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
    padding: '6px 8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    border: '1px solid #e0e0e0'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  },
  legendColor: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  legendText: {
    fontSize: '9px',
    fontFamily: 'Poppins, sans-serif',
  },
  emptyMessage: {
    fontSize: '10px',
    color: '#888888',
    fontStyle: 'italic',
    fontFamily: 'Poppins, sans-serif',
    textAlign: 'center',
    padding: '15px',
  }
};

// Category colors for the dots
const categoryColors = {
  skills: '#4CAF50',      // Green
  knowledge: '#2196F3',   // Blue  
  mindset: '#FF9800',     // Orange
  instruments: '#9C27B0', // Purple
};

// Fixed hex shades per category and frequency level (match PDF)
const categoryFrequencyShades = {
  skills: {
    low: '#b6dab8',
    medium: '#71ae74',
    high: '#0E5A24',
  },
  knowledge: {
    low: '#a6d0f2',
    medium: '#58a0d9',
    high: '#0B3C73',
  },
  mindset: {
    low: '#f7d29b',
    medium: '#dfa04b',
    high: '#7A3F00',
  },
  instruments: {
    low: '#d0a6d7',
    medium: '#a75fb3',
    high: '#49065F',
  },
};

// Helper function to get visual styles based on frequency
const getFrequencyStyles = (competency, categoryColor) => {
  if (!competency.isSelected) {
    return {
      backgroundColor: '#ccc',
      borderColor: 'transparent',
      borderWidth: '1px',
    };
  }

  const frequency = competency.frequency;
  
  // High frequency (67-100%)
  const categoryKey = competency.category;
  const shades = categoryFrequencyShades[categoryKey] || categoryFrequencyShades.skills;
  if (frequency >= 67) {
    return {
      backgroundColor: shades.high,
      borderColor: shades.high,
      borderWidth: '4px',
      boxShadow: `0 0 6px ${categoryColor}60`
    };
  } else if (frequency >= 34) {
    return {
      backgroundColor: shades.medium,
      borderColor: shades.medium,
      borderWidth: '2px',
    };
  } else {
    return {
      backgroundColor: shades.low,
      borderColor: shades.low,
      borderWidth: '1px',
    };
  }
};

const CohortCinemaMap = ({ cohortResponses }) => {
  const [hoveredCompetency, setHoveredCompetency] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoverTimeout, setHoverTimeout] = useState(null);

  // Fetch all competencies from the system
  const { data: allCompetenciesData, isLoading, error } = useAllCompetencies();

  // Get competency IDs that were selected in this cohort
  const selectedCompetencyIds = useMemo(() => {
    if (!cohortResponses?.competency_counts) return new Set();
    return new Set(cohortResponses.competency_counts.map(item => item.competency_id));
  }, [cohortResponses]);

  // Get competency count map and calculate frequencies
  const competencyData = useMemo(() => {
    if (!cohortResponses?.competency_counts || !cohortResponses?.session_count) {
      return { countMap: new Map(), frequencyMap: new Map() };
    }
    
    const countMap = new Map();
    const frequencyMap = new Map();
    const totalParticipants = cohortResponses.session_count;
    
    cohortResponses.competency_counts.forEach(item => {
      const count = item.count;
      const percentage = (count / totalParticipants) * 100;
      
      countMap.set(item.competency_id, count);
      frequencyMap.set(item.competency_id, percentage);
    });
    
    return { countMap, frequencyMap };
  }, [cohortResponses]);

  // Process all competencies and group by phase
  const competenciesByPhase = useMemo(() => {
    if (!allCompetenciesData) return {};

    const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
    const grouped = {};

    // Initialize each phase
    phases.forEach(phase => {
      grouped[phase] = [];
    });

    // Group competencies by phase
    allCompetenciesData.forEach(comp => {
      const phase = comp.abilities?.phase || 'Unknown';
      const competency = {
        id: comp.id,
        name: comp.name,
        category: comp.category,
        phase: phase,
        ability: comp.abilities?.name || 'Unknown',
        isSelected: selectedCompetencyIds.has(comp.id),
        count: competencyData.countMap.get(comp.id) || 0,
        frequency: competencyData.frequencyMap.get(comp.id) || 0
      };

      if (grouped[phase]) {
        grouped[phase].push(competency);
      } else {
        // Handle unknown phases
        if (!grouped['Unknown']) grouped['Unknown'] = [];
        grouped['Unknown'].push(competency);
      }
    });

    return grouped;
  }, [allCompetenciesData, selectedCompetencyIds, competencyData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          Loading all competencies...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          Error loading competencies: {error.message}
        </div>
      </div>
    );
  }

  if (!allCompetenciesData || allCompetenciesData.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          No competencies available in the system
        </div>
      </div>
    );
  }

  if (!cohortResponses || !cohortResponses.competency_counts || cohortResponses.competency_counts.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          No competencies selected in this cohort yet
        </div>
      </div>
    );
  }

  const handleMouseEnter = (competency, event) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    // Update immediately when entering a new dot
    setHoveredCompetency(competency);
    setMousePosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleMouseLeave = () => {
    // Add a small delay before hiding to prevent flickering
    const timeout = setTimeout(() => {
      setHoveredCompetency(null);
    }, 100);
    setHoverTimeout(timeout);
  };

  const handleMouseMove = (event) => {
    setMousePosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  return (
    <div style={styles.container}>
      {/* Competencies grouped by phases with spacing */}
      <div style={styles.competencyGrid}>
        {Object.entries(competenciesByPhase).map(([phase, competencies]) => {
          if (!competencies || competencies.length === 0) return null;
          
          return (
            <div key={phase} style={styles.phaseGroup}>
              {competencies.map((competency, index) => {
                const categoryColor = categoryColors[competency.category] || '#666666';
                const frequencyStyles = getFrequencyStyles(competency, categoryColor);
                const isHovered = hoveredCompetency?.id === competency.id;
                
                return (
                  <div
                    key={`${competency.id}-${index}`}
                    style={{
                      ...styles.competencyDot,
                      ...frequencyStyles,
                      ...(isHovered ? styles.competencyDotHover : {}),
                      ...(isHovered ? { borderColor: categoryColor, borderWidth: '2px' } : {})
                    }}
                    onMouseEnter={(e) => handleMouseEnter(competency, e)}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                    title={competency.name}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Floating tooltip */}
      {hoveredCompetency && (
        <div
          style={{
            position: 'fixed',
            left: mousePosition.x - 80, // Center tooltip over cursor (wider)
            top: mousePosition.y - 60, // Position higher to prevent overlap with cursor
            backgroundColor: '#333333',
            color: 'white',
            padding: '6px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontFamily: 'Poppins, sans-serif',
            zIndex: 1000,
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            maxWidth: '160px',
            lineHeight: '1.3',
            pointerEvents: 'none',
          }}
        >
          <div>{hoveredCompetency.name}</div>
          <div style={{ fontSize: '9px', opacity: 0.8, marginTop: '2px' }}>
            {hoveredCompetency.category} • {hoveredCompetency.phase}
          </div>
          <div style={{ fontSize: '9px', opacity: 0.8, marginTop: '2px' }}>
            {hoveredCompetency.isSelected 
              ? `Selected by ${hoveredCompetency.count} participant(s) (${Math.round(hoveredCompetency.frequency)}%)`
              : 'Not Selected'
            }
          </div>
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '3px solid transparent',
              borderRight: '3px solid transparent',
              borderTop: '3px solid #333333',
              pointerEvents: 'none',
            }}
          ></div>
        </div>
      )}

      {/* Compact legend */}
      <div style={styles.legend}>
        {/* Category colors */}
        {Object.entries(categoryColors).map(([category, color]) => (
          <div key={category} style={styles.legendItem}>
            <div style={{
              ...styles.legendColor,
              backgroundColor: color,
              border: '1px solid #ffffff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}></div>
            <span style={{ 
              ...styles.legendText,
              textTransform: 'capitalize'
            }}>
              {category}
            </span>
          </div>
        ))}
        
        {/* Frequency indicators (match PDF shades for skills as example) */}
        <div style={{ ...styles.legendItem, marginLeft: '10px' }}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: categoryFrequencyShades.skills.high,
            border: `1px solid ${categoryFrequencyShades.skills.high}`,
          }}></div>
          <span style={styles.legendText}>High (67-100%)</span>
        </div>
        
        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: categoryFrequencyShades.skills.medium,
            border: `1px solid ${categoryFrequencyShades.skills.medium}`,
          }}></div>
          <span style={styles.legendText}>Medium (34-66%)</span>
        </div>
        
        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: categoryFrequencyShades.skills.low,
            border: `1px solid ${categoryFrequencyShades.skills.low}`,
          }}></div>
          <span style={styles.legendText}>Low (1-33%)</span>
        </div>
        
        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: '#c0c0c0',
            border: '1px solid #4CAF50',
            opacity: 0.3
          }}></div>
          <span style={styles.legendText}>Not selected</span>
        </div>
      </div>
    </div>
  );
};

export default CohortCinemaMap;
